import { useEffect, useState } from "react";
import SchoololaLoader from "@/components/SchoololaLoader";
import { useLoopProgress, usePrefersReducedMotion } from "@/hooks/use-loop-progress";

interface BrandLoaderProps {
  /** Real percentage 0-100. Omit for an indeterminate looping bar. */
  progress?: number;
  label?: string;
  width?: number;
  fullScreen?: boolean;
  delayMs?: number;
  /** Set false to fade the loader out over ~150ms before it disappears. */
  show?: boolean;
}

/**
 * App-wide branded loading indicator.
 * Wraps SchoololaLoader with indeterminate progress, reduced-motion handling
 * and a 150ms fade-out.
 */
export function BrandLoader({
  progress,
  label = "Loading",
  width = 260,
  fullScreen = false,
  delayMs = 200,
  show = true,
}: BrandLoaderProps) {
  const reduced = usePrefersReducedMotion();
  const looped = useLoopProgress(2500, progress === undefined);
  const [mounted, setMounted] = useState(true);

  useEffect(() => {
    if (show) {
      setMounted(true);
      return;
    }
    const t = setTimeout(() => setMounted(false), 150);
    return () => clearTimeout(t);
  }, [show]);

  if (!mounted) return null;

  const pct = progress === undefined ? looped : Math.max(0, Math.min(100, progress));

  return (
    <div
      style={{
        opacity: show ? 1 : 0,
        transition: "opacity 150ms ease-out",
        ...(fullScreen
          ? { position: "fixed" as const, inset: 0, zIndex: 50 }
          : { display: "contents" as const }),
      }}
    >
      <SchoololaLoader
        progress={pct}
        label={label}
        width={Math.max(160, width)}
        fullScreen={fullScreen}
        delayMs={delayMs}
        key={reduced ? "reduced" : "motion"}
      />
    </div>
  );
}

/** Small inline loader for saving / row refresh states. */
export function InlineBrandLoader({ delayMs = 200 }: { delayMs?: number }) {
  const looped = useLoopProgress(2500, true);
  return (
    <SchoololaLoader progress={looped} label="" width={160} delayMs={delayMs} />
  );
}

export default BrandLoader;
